import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchPropertyById, 
  selectCurrentProperty, 
  selectCurrentPropertyLoading, 
  selectPropertyError, 
  clearCurrentProperty 
} from '../redux/slices/propertySlice';
import { contactAPI } from '../services/api';

const PropertyDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  
  const property = useSelector(selectCurrentProperty);
  const loading = useSelector(selectCurrentPropertyLoading);
  const error = useSelector(selectPropertyError);
  
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const videoRef = useRef(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchPropertyById(id));
    }
    return () => {
      dispatch(clearCurrentProperty());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (property?.title) {
      setContactForm(prev => ({
        ...prev,
        message: `I'm interested in ${property.title}. Please contact me with more details.`
      }));
    }
  }, [property?.title]);

  // Image URL construction
  const getImageUrl = (imageData) => {
    if (!imageData) return '/images/property-placeholder.jpg';

    if (typeof imageData === 'string' && imageData.startsWith('http')) {
      return imageData;
    }

    if (typeof imageData === 'object' && imageData.url) {
      return getImageUrl(imageData.url);
    }

    if (typeof imageData === 'string') {
      if (imageData.includes('/uploads/properties/')) {
        const filename = imageData.split('/uploads/properties/').pop();
        return `${process.env.REACT_APP_API_URL}/uploads/properties/${filename}`;
      }

      if (imageData.startsWith('/uploads/')) {
        return `${process.env.REACT_APP_API_URL}${imageData}`;
      }

      return `${process.env.REACT_APP_API_URL}/uploads/properties/${imageData}`;
    }

    return '/images/property-placeholder.jpg';
  };

  const getPropertyImages = (property) => {
    if (!property) return [];

    const images = [];

    if (property.imageUrl) {
      images.push(getImageUrl(property.imageUrl));
    } else if (property.featuredImage) {
      images.push(getImageUrl(property.featuredImage));
    }

    if (property.images && Array.isArray(property.images)) {
      property.images.forEach(img => {
        const imageUrl = getImageUrl(img);
        if (imageUrl && !images.includes(imageUrl)) {
          images.push(imageUrl);
        }
      });
    }

    if (images.length === 0) {
      images.push('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80');
    }

    return images;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await contactAPI.sendMessage({
        ...contactForm,
        subject: `Inquiry about ${property?.title || 'Property'} (Property ID: ${property?.id || id})`
      });
      alert('Message sent successfully!');
      setShowContactForm(false);
      setContactForm({
        name: '',
        email: '',
        phone: '',
        message: `I'm interested in ${property?.title || 'this property'}. Please contact me with more details.`
      });
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const formatPrice = (price) => {
    if (!price) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(parseFloat(price));
  };

  // NEW: Format area with smart display logic
  const formatArea = (area) => {
    if (!area) return 'N/A';
    return `${parseFloat(area).toLocaleString('en-IN')} sq ft`;
  };

  // NEW: Get primary display area (carpet > super built-up > built-up > square footage)
  const getPrimaryArea = (property) => {
    if (property.carpetArea) return { value: property.carpetArea, type: 'Carpet Area' };
    if (property.superBuiltUpArea) return { value: property.superBuiltUpArea, type: 'Super Built-up Area' };
    if (property.builtUpArea) return { value: property.builtUpArea, type: 'Built-up Area' };
    if (property.squareFootage) return { value: property.squareFootage, type: 'Total Area' };
    return null;
  };

  const calculatePropertyAge = (yearBuilt) => {
    if (!yearBuilt) return null;
    try {
      const currentYear = new Date().getFullYear();
      const age = currentYear - parseInt(yearBuilt);
      return age >= 0 ? age : null;
    } catch {
      return null;
    }
  };

  const getPropertyCondition = (age) => {
    if (!age) return { condition: 'Unknown', color: 'gray' };
    if (age <= 5) return { condition: 'New', color: 'green' };
    if (age <= 15) return { condition: 'Modern', color: 'blue' };
    if (age <= 30) return { condition: 'Well-maintained', color: 'yellow' };
    if (age <= 50) return { condition: 'Established', color: 'orange' };
    return { condition: 'Historic', color: 'purple' };
  };

  const getFileExtension = (url) => {
    if (!url) return null;
    
    try {
      const urlStr = String(url);
      const cleanUrl = urlStr.split(/[?#]/)[0];
      const lastSlashIndex = cleanUrl.lastIndexOf('/');
      const filename = lastSlashIndex >= 0 ? cleanUrl.slice(lastSlashIndex + 1) : cleanUrl;
      const match = filename.match(/\.(mp4|webm|ogg|m3u8)$/i);
      
      return match ? match[1].toLowerCase() : null;
    } catch (error) {
      console.warn('getFileExtension error:', error);
      return null;
    }
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    try {
      const urlStr = String(url);
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = urlStr.match(regExp);
      return (match && match[2] && match[2].length === 11) ? match[2] : null;
    } catch {
      return null;
    }
  };

  // Property Video Panel Component
  const PropertyVideoPanel = ({ videoUrl, title }) => {
    const [iframeError, setIframeError] = useState(false);
    const ext = getFileExtension(videoUrl);
    const isDirectFile = Boolean(ext);
    const youtubeId = getYouTubeVideoId(videoUrl);

    if (!videoUrl) {
      return (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-xl font-semibold">Property Video</h3>
          </div>
          <div className="p-4">
            <div className="text-gray-600 text-sm">No video URL available for this property.</div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-xl font-semibold">Property Video</h3>
        </div>

        <div className="p-4">
          {isDirectFile ? (
            <div className="w-full flex justify-center">
              <div className="relative w-full" style={{ maxWidth: '640px' }}>
                <div style={{ aspectRatio: '16 / 9' }}>
                  <video
                    controls
                    preload="metadata"
                    className="w-full h-full rounded-lg bg-black"
                    title={title || 'Property video'}
                  >
                    <source
                      src={videoUrl}
                      type={ext === 'm3u8' ? 'application/vnd.apple.mpegURL' : `video/${ext === 'ogg' ? 'ogg' : ext}`}
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          ) : youtubeId ? (
            <div className="w-full flex justify-center">
              <div className="relative w-full" style={{ maxWidth: '640px' }}>
                <div style={{ aspectRatio: '16 / 9' }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                    title={title || 'Property video'}
                    className="w-full h-full rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="w-full flex justify-center">
                <div className="relative w-full" style={{ maxWidth: '640px' }}>
                  <div style={{ aspectRatio: '16 / 9' }}>
                    <iframe
                      src={videoUrl}
                      title={title || 'Property video'}
                      className="w-full h-full rounded-lg"
                      loading="lazy"
                      allow="autoplay; fullscreen; picture-in-picture"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-presentation"
                      onError={() => setIframeError(true)}
                    />
                  </div>
                </div>
              </div>
              {iframeError && (
                <p className="mt-3 text-xs text-red-600">
                  Video could not be embedded due to site restrictions. Use the link below to open in a new tab.
                </p>
              )}
            </>
          )}

          <div className="mt-3 text-right">
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 text-sm font-semibold"
            >
              Open original link ↗
            </a>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏠</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || "Property Not Found"}
          </h2>
          <Link 
            to="/properties" 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
          >
            Back to Properties
          </Link>
        </div>
      </div>
    );
  }

  const propertyImages = getPropertyImages(property);
  const propertyAge = calculatePropertyAge(property.yearBuilt);
  const conditionInfo = getPropertyCondition(propertyAge);
  const primaryArea = getPrimaryArea(property);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li>
                <Link to="/" className="text-gray-700 hover:text-blue-600">Home</Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <Link to="/properties" className="ml-1 text-gray-700 hover:text-blue-600">Properties</Link>
                </div>
              </li>
              <li aria-current="page">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span className="ml-1 text-gray-500 truncate">{property.title}</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Property Images */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
              <div className="relative">
                <img 
                  src={propertyImages[activeImageIndex]}
                  alt={`${property.title} - Image ${activeImageIndex + 1}`}
                  className="w-full h-96 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=800&q=80';
                  }}
                />
                
                {/* Image Navigation */}
                {propertyImages.length > 1 && (
                  <>
                    <button 
                      onClick={() => setActiveImageIndex(prev => prev > 0 ? prev - 1 : propertyImages.length - 1)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => setActiveImageIndex(prev => prev < propertyImages.length - 1 ? prev + 1 : 0)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                
                {/* Image Counter */}
                <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {activeImageIndex + 1} / {propertyImages.length}
                </div>

                {/* Property Status Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-bold text-white backdrop-blur-sm ${
                    property.status === 'available' ? 'bg-green-500/90' : 
                    property.status === 'sold' ? 'bg-red-500/90' :
                    property.status === 'rented' ? 'bg-blue-500/90' : 'bg-yellow-500/90'
                  }`}>
                    {property.status?.toUpperCase()}
                  </span>
                </div>

                {/* Featured Badge */}
                {(property.isFeatured || property.featured) && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      ⭐ FEATURED
                    </span>
                  </div>
                )}
              </div>
              
              {/* Thumbnail Images */}
              {propertyImages.length > 1 && (
                <div className="p-4 bg-gray-50">
                  <div className="flex space-x-2 overflow-x-auto">
                    {propertyImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          activeImageIndex === index 
                            ? 'border-blue-500 ring-2 ring-blue-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img 
                          src={image}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=200&q=80';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Property Details */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.title}</h1>
                <p className="text-gray-600 flex items-center text-lg">
                  📍 {property.address || 'N/A'}, {property.city || 'N/A'}, {property.state || 'N/A'} {property.zipCode || ''}
                </p>
              </div>

              {/* NEW: Enhanced Property Tags with new fields */}
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold capitalize">
                  {property.propertyType || 'Property'}
                </span>
                <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold capitalize">
                  {property.listingType || 'For Sale'}
                </span>
                {/* NEW: Property Category */}
                {property.propertyCategory && (
                  <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full font-semibold">
                    {property.propertyCategory}
                  </span>
                )}
                {/* NEW: Project Status */}
                {property.projectStatus && (
                  <span className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full font-semibold">
                    {property.projectStatus}
                  </span>
                )}
                {/* NEW: Furnishing */}
                {property.furnishing && (
                  <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                    {property.furnishing}
                  </span>
                )}
                {propertyAge && (
                  <span className={`bg-${conditionInfo.color}-100 text-${conditionInfo.color}-800 px-4 py-2 rounded-full font-semibold`}>
                    {conditionInfo.condition} ({propertyAge} years)
                  </span>
                )}
                {/* NEW: Katha Type */}
                {property.katha && (
                  <span className="bg-pink-100 text-pink-800 px-4 py-2 rounded-full font-semibold">
                    {property.katha}
                  </span>
                )}
              </div>

              {/* Price Section - Mobile Responsive */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-4 md:p-6 mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 break-words">
                      {formatPrice(property.price)}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base">
                      INR
                    </p>
                    {/* NEW: Available From */}
                    {property.availableFrom && (
                      <p className="text-gray-600 text-sm mt-2">
                        📅 Available from: <span className="font-semibold">{property.availableFrom}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-left sm:text-right">
                    <p className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">Price per sq ft</p>
                    <p className="text-lg sm:text-xl font-semibold text-gray-900 break-words">
                      {primaryArea ? formatPrice(property.price / primaryArea.value) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* NEW: Builder Information */}
              {property.builder && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-6">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🏗️</span>
                    <div>
                      <p className="text-sm text-gray-600">Builder/Developer</p>
                      <p className="text-lg font-bold text-gray-900">{property.builder}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* UPDATED: Property Features Grid with new fields */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-2">🛏️</div>
                  <div className="text-xl font-semibold">{property.bedrooms || 0}</div>
                  <div className="text-sm text-gray-600">Bedrooms</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-2">🚿</div>
                  <div className="text-xl font-semibold">{property.bathrooms || 0}</div>
                  <div className="text-sm text-gray-600">Bathrooms</div>
                </div>
                {/* NEW: Balcony */}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-2">🏡</div>
                  <div className="text-xl font-semibold">{property.balcony || 0}</div>
                  <div className="text-sm text-gray-600">Balconies</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl mb-2">🚗</div>
                  <div className="text-xl font-semibold">{property.parkingSpaces || 0}</div>
                  <div className="text-sm text-gray-600">Parking</div>
                </div>
              </div>

              {/* NEW: Comprehensive Area Details Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="mr-2">📐</span> Area Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {property.carpetArea && (
                    <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
                      <p className="text-sm text-gray-600">Carpet Area</p>
                      <p className="text-2xl font-bold text-gray-900">{formatArea(property.carpetArea)}</p>
                      <p className="text-xs text-gray-500 mt-1">Actual usable space</p>
                    </div>
                  )}
                  {property.superBuiltUpArea && (
                    <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
                      <p className="text-sm text-gray-600">Super Built-up Area</p>
                      <p className="text-2xl font-bold text-gray-900">{formatArea(property.superBuiltUpArea)}</p>
                      <p className="text-xs text-gray-500 mt-1">Includes common areas</p>
                    </div>
                  )}
                  {property.builtUpArea && (
                    <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                      <p className="text-sm text-gray-600">Built-up Area</p>
                      <p className="text-2xl font-bold text-gray-900">{formatArea(property.builtUpArea)}</p>
                      <p className="text-xs text-gray-500 mt-1">Includes walls</p>
                    </div>
                  )}
                  {property.plotArea && (
                    <div className="bg-white rounded-lg p-4 border-l-4 border-yellow-500">
                      <p className="text-sm text-gray-600">Plot Area</p>
                      <p className="text-2xl font-bold text-gray-900">{formatArea(property.plotArea)}</p>
                      <p className="text-xs text-gray-500 mt-1">Total land area</p>
                    </div>
                  )}
                </div>
              </div>

              {/* NEW: Floor Information */}
              {(property.floorNo || property.totalFloors) && (
                <div className="bg-gray-50 rounded-xl p-6 mb-8">
                  <h3 className="text-xl font-semibold mb-4 flex items-center">
                    <span className="mr-2">🏢</span> Floor Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {property.floorNo && (
                      <div>
                        <p className="text-sm text-gray-600">Floor Number</p>
                        <p className="text-2xl font-bold text-gray-900">{property.floorNo}</p>
                      </div>
                    )}
                    {property.totalFloors && (
                      <div>
                        <p className="text-sm text-gray-600">Total Floors</p>
                        <p className="text-2xl font-bold text-gray-900">{property.totalFloors}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Video Section CTA */}
              {property.videoUrl && (
                <div className="mb-8">
                  <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-2xl mr-3">🎥</div>
                      <div>
                        <h4 className="font-semibold">Property Video Tour</h4>
                        <p className="text-sm text-gray-600">Inline video available below the contact form.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                    >
                      Jump to Video →
                    </button>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4">Property Description</h3>
                <p className="text-gray-700 leading-relaxed">
                  {property.description || 'No description available for this property.'}
                </p>
              </div>

              {/* UPDATED: Additional Details with new fields */}
              <div className="border-t pt-8">
                <h3 className="text-xl font-semibold mb-4">Additional Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {property.yearBuilt && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Year Built:</span>
                      <span className="font-semibold">{property.yearBuilt}</span>
                    </div>
                  )}
                  {/* NEW: Door Facing */}
                  {property.doorFacing && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Door Facing:</span>
                      <span className="font-semibold">{property.doorFacing}</span>
                    </div>
                  )}
                  {/* NEW: Servant Room */}
                  {property.serventRoom !== undefined && property.serventRoom !== null && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Servant Room:</span>
                      <span className="font-semibold">{property.serventRoom > 0 ? 'Yes' : 'No'}</span>
                    </div>
                  )}
                  {property.lotSize && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Lot Size:</span>
                      <span className="font-semibold">{property.lotSize} sq ft</span>
                    </div>
                  )}
                  {property.propertyTax && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Property Tax:</span>
                      <span className="font-semibold">{formatPrice(property.propertyTax)}/year</span>
                    </div>
                  )}
                  {property.hoaFees && (
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">HOA Fees:</span>
                      <span className="font-semibold">{formatPrice(property.hoaFees)}/month</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 sticky top-8">
              <h3 className="text-xl text-black font-semibold mb-4">Contact About This Property</h3>
              
             

              {!showContactForm ? (
                <div className="space-y-3">
                  <button 
                    onClick={() => setShowContactForm(true)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
                  >
                    📧 Send Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Your Phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <textarea
                    rows="4"
                    placeholder="Your Message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  ></textarea>
                  <div className="flex space-x-3">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Send Message
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowContactForm(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Video Panel - Full width below contact form */}
        {property.videoUrl && (
          <div ref={videoRef} className="mt-8">
            <PropertyVideoPanel videoUrl={property.videoUrl} title={property.title} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyDetail;
